import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { useTheme } from '@emotion/react';
import { ratio } from './ContentHeaderCover';
import { mainBg } from '../../styles/Main';
import { TeaserDataType } from './ContentHeader';
import ContentPlayerShelf from './ContentPlayerShelf';
import HlsPlayer from '../Widgets/WidgetManager/WidgetsPlayer/HlsPlayer';
import Mp4Player from '../Widgets/WidgetManager/WidgetsPlayer/Mp4Player';
import useInView from '../../useInView';

export default function ContentPlayer({ teaserData, showGradient = true, fixedHeight, pauseOutOfView = true }:
{ teaserData: TeaserDataType; showGradient?: boolean; fixedHeight?: string; pauseOutOfView?: boolean }) {
  const theme = useTheme();
  const [isMobile, setIsMobile] = useState(true);
  const [showVideo, setShowVideo] = useState(false);

  const wrapperRef = useRef<HTMLDivElement>(null);
  const inView = useInView(wrapperRef);
  // Pause when scrolled out of the viewport and resume when it scrolls back into view.
  const videoVisible = showVideo && (!pauseOutOfView || inView);

  const videoUrl = teaserData?.videoUrl || '';
  const poster = (teaserData?.poster || '') as string;
  const posterAlt = teaserData?.posterAlt || 'Filmnet';
  const isHls = teaserData?.video_type === 'hls';

  const resizeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    handleShowVideo();
    window.addEventListener('resize', throttledHandle, { passive: true });
    return () => {
      window.removeEventListener('resize', throttledHandle);
      if (resizeTimerRef.current) {
        clearTimeout(resizeTimerRef.current);
        resizeTimerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const throttledHandle = () => {
    if (resizeTimerRef.current !== null) return;
    resizeTimerRef.current = setTimeout(() => {
      handleShowVideo();
      resizeTimerRef.current = null;
    }, 100);
  };

  const handleShowVideo = () => {
    const mobileBreakPoint = Number(theme.breakpoints.md) >= Number(window.innerWidth);
    setShowVideo(!!videoUrl && !mobileBreakPoint);
    setIsMobile(mobileBreakPoint);
  };

  const handleEnded = useCallback(() => {
    setShowVideo(false);
  }, []);

  return (
    <Wrapper ref={wrapperRef} fixedHeight={fixedHeight}>
      {videoUrl && !isMobile && (
        isHls ? (
          <HlsPlayer
            active
            previewChrome={false}
            videoVisible={videoVisible}
            onRequestVideoVisible={() => setShowVideo(true)}
            source={videoUrl}
            poster={poster}
            autoplay={showVideo}
            onEnded={handleEnded}
          >
            {(api) => (
              <ContentPlayerShelf
                isPlaying={api.playing}
                isMuted={api.muted}
                onTogglePlay={() => void api.togglePlay()}
                onToggleMute={api.toggleMute}
              />
            )}
          </HlsPlayer>
        ) : (
          <Mp4Player
            active
            previewChrome={false}
            videoVisible={videoVisible}
            onRequestVideoVisible={() => setShowVideo(true)}
            src={videoUrl}
            poster={poster}
            autoplay={showVideo}
            onEnded={handleEnded}
          >
            {(api) => (
              <ContentPlayerShelf
                isPlaying={api.playing}
                isMuted={api.muted}
                onTogglePlay={() => void api.togglePlay()}
                onToggleMute={api.toggleMute}
              />
            )}
          </Mp4Player>
        )
      )}
      <Image
        src={poster}
        alt={posterAlt}
        loading="eager"
        {...({ fetchpriority: 'high' } as any)}
      />
      {showGradient && (
        <>
          <GradientTop />
          <GradientX />
          <GradientY />
        </>
      )}
    </Wrapper>
  );
}

const Wrapper = styled.div<{ fixedHeight?: string }>(({ theme, fixedHeight }) => ({
  width: '100%',
  position: 'relative',
  overflow: 'hidden',
  zIndex: 0,
  ...(fixedHeight
    ? { height: fixedHeight }
    : {
      paddingTop: `${ratio.xs * 100}%`,
      [theme.breakpoints.up('md')]: {
        paddingTop: `${ratio.md * 100}%`,
        top: 0,
        left: 0,
      },
    }),
}));

// @emotion/is-prop-valid 1.1 strips unknown attrs like `fetchpriority`.
const Image = styled('img', {
  shouldForwardProp: (prop) => prop !== 'theme' && prop !== 'as',
})({
  position: 'absolute',
  inset: 0,
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  transition: 'image .3s ease-in-out',
});

export const GradientY = styled.div(({ theme }) => ({
  pointerEvents: 'none',
  [theme.breakpoints.up('sm')]: {
    background: `linear-gradient(to left, ${theme.palette.background[mainBg][0]}, rgba(0,0,0,0))`,
    height: '100%',
    width: '20%',
    position: 'absolute',
    bottom: 0,
    right: -1,
    zIndex: 4,
  },
}));

export const GradientX = styled.div(() => ({
  background: 'linear-gradient(180deg, rgba(0, 0, 0, 0) 50%, rgba(28, 28, 28, 1) 100%)',
  position: 'absolute',
  bottom: -1,
  width: '100%',
  height: '20%',
  zIndex: 4,
  pointerEvents: 'none',
}));

export const GradientTop = styled.div(() => ({
  background: 'linear-gradient(0deg, rgba(0, 0, 0, 0) 50%, rgba(28, 28, 28, 1) 100%)',
  position: 'absolute',
  top: -1,
  width: '100%',
  height: '20%',
  zIndex: 4,
  pointerEvents: 'none',
}));
